import { createFileRoute, Navigate } from '@tanstack/react-router';

import { AgencyContextCard } from '@apps/web/components/settings/agency_context_card';
import { useAgencyContextDocument } from '@apps/web/components/settings/agency_context_data';
import {
    SettingsLoadingState,
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';

export const Route = createFileRoute('/_app/settings/agency-context')({
    component: AgencyContextSettingsPage,
});

function AgencyContextSettingsPage() {
    const { organizationId, isLoading, gates } = useSettingsGates();

    if (isLoading) {
        return <SettingsLoadingState />;
    }
    if (organizationId === null) {
        return null;
    }
    if (!gates.agencyGuide) {
        return <Navigate to="/settings" replace={true} />;
    }
    return (
        <SettingsPageShell
            title="Agency Guide"
            description="Tell Phoebe how your agency works. Phoebe uses this guide on calls, texts, and chat."
        >
            <AgencyContextSection organizationId={organizationId} />
        </SettingsPageShell>
    );
}

function AgencyContextSection({
    organizationId,
}: {
    readonly organizationId: string;
}) {
    const {
        document,
        loadFailed,
        isLoading,
        isSaving,
        saveFailed,
        conflictCount,
        save,
    } = useAgencyContextDocument(organizationId);

    // Loading, failed, and empty are three different states.
    if (isLoading) {
        return <SettingsLoadingState />;
    }
    if (loadFailed || document === undefined) {
        return (
            <p className="text-muted-foreground text-sm">
                Couldn’t load your agency guide. Phoebe is still using whatever
                was saved last — nothing has changed.
            </p>
        );
    }
    return (
        <AgencyContextCard
            // Remount on a conflict so the editor reseeds onto the winning
            // document rather than holding a draft nobody's version matches.
            key={`${organizationId}:${conflictCount}`}
            document={document}
            isSaving={isSaving}
            saveFailed={saveFailed}
            hadConflict={conflictCount > 0}
            onSave={save}
        />
    );
}
